import React from 'react';
import { DatePicker, Dropdown, Menu } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Button from './Button';
import { CalenderIconDark } from '../../assets';

dayjs.extend(relativeTime);

const DateRangeFilter = ({ startDate, endDate, onDateChange }) => {
    const [selectedRange, setSelectedRange] = React.useState('Select Range');

    const calculateDuration = () => {
        if (!startDate || !endDate) return 'Select a date range';

        const start = dayjs(startDate, 'DD/MM/YYYY');
        const end = dayjs(endDate, 'DD/MM/YYYY');

        const diffDays = end.diff(start, 'day');
        if (diffDays === 0) return 'Same day';
        if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''}`;

        const diffMonths = end.diff(start, 'month');
        if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''}`;

        const diffYears = end.diff(start, 'year');
        return `${diffYears} year${diffYears > 1 ? 's' : ''}`;
    };

    const handleQuickSelect = (rangeKey) => {
        const today = dayjs();
        let start, end = today.format('DD/MM/YYYY');
        let rangeText = 'Select Range';

        switch (rangeKey) {
            case '1month':
                start = today.subtract(1, 'month').format('DD/MM/YYYY');
                rangeText = 'Past 1 Month';
                break;
            case '3months':
                start = today.subtract(3, 'months').format('DD/MM/YYYY');
                rangeText = 'Past 3 Months';
                break;
            case '6months':
                start = today.subtract(6, 'months').format('DD/MM/YYYY');
                rangeText = 'Past 6 Months';
                break;
            case '1year':
                start = today.subtract(1, 'year').format('DD/MM/YYYY');
                rangeText = 'Past 1 Year';
                break;
            case 'custom':
                // Reset to empty for custom selection
                start = '';
                end = '';
                rangeText = 'Custom Range';
                break;
            default:
                start = today.subtract(1, 'month').format('DD/MM/YYYY');
                rangeText = 'Past 1 Month';
        }

        setSelectedRange(rangeText);
        onDateChange({ startDate: start, endDate: end });
    };

    const menu = (
        <Menu>
            <Menu.Item key="1month" onClick={() => handleQuickSelect('1month')}>
                Past 1 Month
            </Menu.Item>
            <Menu.Item key="3months" onClick={() => handleQuickSelect('3months')}>
                Past 3 Months
            </Menu.Item>
            <Menu.Item key="6months" onClick={() => handleQuickSelect('6months')}>
                Past 6 Months
            </Menu.Item>
            <Menu.Item key="1year" onClick={() => handleQuickSelect('1year')}>
                Past 1 Year
            </Menu.Item>
            <Menu.Item key="custom" onClick={() => handleQuickSelect('custom')}>
                Custom Range
            </Menu.Item>
        </Menu>
    );

    return (
        <div className="flex items-center gap-2">
            <Dropdown overlay={menu} trigger={['click']}>
                <Button
                    buttonProps={{ type: 'primary-square' }}
                    text={selectedRange}
                    buttonIcon={CalenderIconDark}
                />
            </Dropdown>
            <DatePicker.RangePicker
                value={[
                    startDate ? dayjs(startDate, 'DD/MM/YYYY') : null,
                    endDate ? dayjs(endDate, 'DD/MM/YYYY') : null
                ]}
                format="DD/MM/YYYY"
                onChange={(dates) => {
                    if (dates && dates.length === 2) {
                        const [start, end] = dates;
                        setSelectedRange('Custom Range');
                        onDateChange({
                            startDate: start.format('DD/MM/YYYY'),
                            endDate: end.format('DD/MM/YYYY')
                        });
                    } else {
                        setSelectedRange('Select Range');
                        onDateChange({
                            startDate: '',
                            endDate: ''
                        });
                    }
                }}
                renderExtraFooter={() => (
                    <div className="p-2 text-center text-sm text-gray-500">
                        {calculateDuration()}
                    </div>
                )}
                className="border border-gray-300 rounded-md h-10"
            />
        </div>
    );
};

export default React.memo(DateRangeFilter);